import { Group } from "./applicationSummary";

export interface ConfirmationData {
    label?: string;
    subLabel?: string;
    value?: string | boolean;
    premium?: string;
    fontWeight?: 'bold' | 'normal';
    showBorder?: boolean;
    coverageLabel?: string;
    type?: 'text' | 'checkbox' | 'download' | 'link';
    color?: 'black' | 'skyblue' | 'green';
    downloadLabel?: string;
    hiddenIfEmpty?: boolean;
    hidden?: boolean;
    hiddenAll?: boolean;
    /** Schema rejection shown under the row. Keeps an empty row visible. */
    errorMessage?: string;
    order?: number;
    coverageClassName?: string;
}

export interface QuoteSelected {
    gender?: string;
    smoker?: boolean;
    country?: string;
    lead?: {
        date_of_birth?: string;
    };
    annual_income?: number;
    product_type?: string;
    term_selected?: string;
    frecuency_selected?: string;
    policy_amount?: number;
    protection?: {
        amount?: number;
        critical_type?: string;
        total_ci_in_frecuency?: number;
        total_ci?: number;
        total_cp_in_frecuency?: number;
        total_cp?: number;
        type?: string;
    } | null;
    add_amount?: number | null;
    ad_amount?: number | null;
    tpd_amount?: number | null;
    coverage_include_amount?: number;
    rop?: boolean;
}

export interface Question {
    label?: string;
    value: string;
    hidden: boolean;
}

export interface LastQuote {
    total_premium?: number;
    rop_amount?: number;
    income_replacement?: any;
    total_premium_in_frecuency?: number;
    coverage_premium?: number;
    rider_optional_total_ad?: number;
    rider_optional_total_tpd?: number;
}

export const removeMapValue = (key: string, map: Map<string, ConfirmationData>): Map<string, ConfirmationData> => {
    const newMap = new Map(map);
    newMap.delete(key);
    return newMap;
};

export const updateMapWithFields = (updateFields: Array<[string, Question]>, cd: Map<string, ConfirmationData>, setLastBorder: boolean = false): Map<string, ConfirmationData> => {
    const newMap = new Map(cd);
    const lastValidKey = updateFields
        .slice(-1)[0]?.[0];
    updateFields.forEach(([key, question]) => {
        const item = newMap.get(key);
        if (item) {
            newMap.set(key, {
                ...item,
                label: item.label ? item.label : question.label,
                value: question.value,
                showBorder: key === lastValidKey ? false : item.showBorder,
                hidden: question.hidden

            });
        }
    });

    // Buscar el último elemento visible (donde hidden es false)
    const visibleKeys = Array.from(newMap.entries())
        .filter(([_, item]) => !item.hidden)
        .map(([key]) => key);

    const lastVisibleKey = visibleKeys[visibleKeys.length - 1];

    if (lastVisibleKey) {
        const lastVisibleItem = newMap.get(lastVisibleKey);
        if (lastVisibleItem) {
            newMap.set(lastVisibleKey, {
                ...lastVisibleItem,
                showBorder: false
            });
        }
    }

    // Mantener el comportamiento original para setLastBorder si es necesario
    if (setLastBorder) {
        const lastKey = Array.from(newMap.keys()).pop();
        if (lastKey) {
            const lastItem = newMap.get(lastKey);
            if (lastItem) {
                newMap.set(lastKey, {
                    ...lastItem,
                    showBorder: setLastBorder
                });
            }
        }
    }

    return newMap;
};


export const updateMapWithFields2 = (updateFields: Array<[string, string | null]>, cd: Map<string, ConfirmationData>, setLastBorder: boolean = false): Map<string, ConfirmationData> => {
    const newMap = new Map(cd);

    updateFields.forEach(([key, value]) => {
        if (value !== null) {
            const item = newMap.get(key);
            if (item) {
                newMap.set(key, {
                    ...item,
                    value,
                    showBorder: item.showBorder
                });
            }
        }
    });

    // Encontrar el último elemento visible (que no esté oculto ni vacío si hiddenIfEmpty está establecido)
    const visibleItems = Array.from(newMap.entries())
        .filter(([_, item]) => {
            // Un elemento es visible si:
            // 1. No está explícitamente oculto (hidden !== true)
            // 2. Si tiene hiddenIfEmpty, entonces debe tener un valor
            return !item.hidden &&
                !(item.hiddenIfEmpty === true &&
                    (item.value === undefined || item.value === "" || item.value === false));
        });

    if (visibleItems.length > 0) {
        const lastVisibleKey = visibleItems[visibleItems.length - 1][0];

        const lastVisibleItem = newMap.get(lastVisibleKey);
        if (lastVisibleItem) {
            newMap.set(lastVisibleKey, {
                ...lastVisibleItem,
                showBorder: setLastBorder
            });
        }
    }

    return newMap;
};

export const createFieldsGroups = (groups: Group[], cd: Map<string, ConfirmationData>, setLastBorder?: boolean): Map<string, ConfirmationData> => {
    const fieldAccumulator = new Map<string, ConfirmationData>();

    groups.forEach((group, index) => {
        // Clonar la estructura de defaultSubstanceItem para cada grupo
        for (const [key, value] of cd) {
            const newKey = key === "group" ? group.group_id : group.group_id + "|" + key;
            const newValue = { ...value }; // Preserva todas las propiedades, incluido hiddenIfEmpty

            // Actualizar valores específicos
            if (key === "group") {
                // Si el label está vacío, usar el título del grupo en español
                newValue.label = value.label === "" ?
                    (group.title?.es || `Grupo ${index + 1}`) :
                    `${value.label} ${index + 1}`;
                newValue.hidden = group.hidden;
            } else {
                const field = group.fields.find(f => f.question_id === key);
                if (field) {
                    newValue.value = field.value || "";
                    // Si el campo o el grupo están ocultos, marcar como oculto
                    newValue.hidden = field.hidden || group.hidden;
                }
            }

            fieldAccumulator.set(newKey, newValue);
        }
    });
    if (setLastBorder !== undefined) {
        const lastKey = Array.from(fieldAccumulator.keys()).pop();
        if (lastKey) {
            const lastItem = fieldAccumulator.get(lastKey);
            if (lastItem) {
                fieldAccumulator.set(lastKey, {
                    ...lastItem,
                    showBorder: setLastBorder
                });
            }
        }
    }

    return fieldAccumulator;
}